// Small shared pieces of the screens: loading and error states, and the
// "say why" prompt every write uses (approve, save, settings).
import { useIonAlert } from "@ionic/react";
import { useCallback } from "react";
import type { ApiError } from "../api/client";

export function Loading({ what = "Loading" }: { what?: string }) {
  return <div className="loading" role="status">{what}…</div>;
}

export function ErrorNote({ error, retry }: { error: ApiError | Error | null; retry?: () => void }) {
  if (!error) return null;
  return (
    <div className="error-card" role="alert">
      {error.message}
      {retry ? <> <button type="button" className="btn btn--sm btn--ghost" onClick={retry}>Try again</button></> : null}
    </div>
  );
}

/** Ask for a reason; resolves to the text, or null if cancelled. Empty reasons aren't accepted. */
export function useReason() {
  const [present] = useIonAlert();
  return useCallback((header: string, message: string, confirm = "Save") =>
    new Promise<string | null>((resolve) => {
      const ask = (warn?: string) => present({
        header,
        message: warn ?? message,
        inputs: [{ name: "reason", type: "textarea", placeholder: "Why? (needed)" }],
        buttons: [
          { text: "Cancel", role: "cancel", handler: () => resolve(null) },
          { text: confirm, role: "confirm", handler: (v: { reason?: string }) => {
            const r = (v?.reason ?? "").trim();
            if (!r) { setTimeout(() => ask("Every change says why. Write a line."), 0); return true; }
            resolve(r);
            return true;
          } }
        ]
      });
      ask();
    }), [present]);
}
